/**
 * Create Subcategory Dialog Component
 * Form to create a new subcategory under a parent category
 */

import { useState } from 'react';
import { useCreateSubcategory } from '../../stores/categories';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface CreateSubcategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentCategoryId: number;
  parentCategoryName: string;
  onSuccess: () => void;
}

export function CreateSubcategoryDialog({
  open,
  onOpenChange,
  parentCategoryId,
  parentCategoryName,
  onSuccess,
}: CreateSubcategoryDialogProps) {
  const [subcategoryName, setSubcategoryName] = useState('');
  const [error, setError] = useState('');

  const createMutation = useCreateSubcategory();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!subcategoryName.trim()) {
      setError('Subcategory name is required');
      return;
    }

    try {
      await createMutation.mutate({
        subcategory_name: subcategoryName.trim(),
        parent_category_id: parentCategoryId,
      });
      handleClose();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create subcategory');
    }
  }

  function handleClose() {
    setSubcategoryName('');
    setError('');
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Subcategory</DialogTitle>
          <DialogDescription>
            Add a new subcategory under "{parentCategoryName}"
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="parent-category">Parent Category</Label>
              <Input
                id="parent-category"
                type="text"
                value={parentCategoryName}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subcategory-name">Subcategory Name *</Label>
              <Input
                id="subcategory-name"
                type="text"
                placeholder="e.g., Laptops, Desks, Paintings"
                value={subcategoryName}
                onChange={(e) => setSubcategoryName(e.target.value)}
                required
                disabled={createMutation.isPending}
                autoFocus
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Subcategory'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
